// ImportLogDialog.tsx
import * as React from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    Paper,
    Typography
} from "@mui/material";
import {CartonGradeCatalogImportLogDTO} from "@/api/data-contracts";
import {getI18n, useTranslation} from "react-i18next";
import {AdminI18N, CommonI18N} from "@/translations";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {formatDateTime, formatDuration, formatInteger} from "@/lib/utils";

interface ImportLogDialogProps {
    open: boolean;
    onClose: () => void;
    importLog: CartonGradeCatalogImportLogDTO;
}

const ImportLogDialog: React.FC<ImportLogDialogProps> = ({open, onClose, importLog}) => {
    const {t} = useTranslation();
    const locale = getI18n().language;

    if (!importLog) {
        return null;
    }

    const startTime = importLog.createdAt ? new Date(importLog.createdAt) : null;
    const importDuration = importLog.importDuration ? importLog.importDuration / 1000 : 0; // Convert to seconds
    const downloadDuration = importLog.downloadDuration ? importLog.downloadDuration / 1000 : 0; // Convert to seconds

    return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t(AdminI18N.cartonGradesImport.page.importLogTitle)}
                    {importLog.success ? (
                            <CheckCircleOutlineIcon color="success" sx={{ml: 1}}/>
                    ) : (
                            <ErrorOutlineIcon color="error" sx={{ml: 1}}/>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* Import-Information */}
                        <Grid size={{xs: 12}}>
                            <Typography variant="h6">{t(AdminI18N.cartonGradesImport.page.importInfoTitle)}</Typography>
                            <Paper elevation={1} sx={{p: 2, mb: 2}}>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.importDate)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {startTime ? formatDateTime(startTime, locale) : "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.importDuration)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatDuration(importDuration + downloadDuration, t)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.importUser)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {importLog.createdBy
                                                    ? `${importLog.createdBy.firstName} ${importLog.createdBy.lastName}`
                                                    : "-"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Kartonsortenstatistik */}
                        <Grid size={{xs: 12}}>
                            <Typography
                                    variant="h6">{t(AdminI18N.cartonGradesImport.page.gradesStatisticsTitle)}</Typography>
                            <Paper elevation={1} sx={{p: 2, mb: 2}}>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.totalGrades)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.cartonGradeCount, locale) || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.updatedGradesValueChanges)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.modifiedCartonGrades, locale) || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.newGradesSinceLastImport)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.createdCartonGrades, locale) || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.removedGradesSinceLastImport)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.deletedCartonGrades, locale) || 0}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Werkestatistik */}
                        <Grid size={{xs: 12}}>
                            <Typography
                                    variant="h6">{t(AdminI18N.cartonGradesImport.page.plantsStatisticsTitle)}</Typography>
                            <Paper elevation={1} sx={{p: 2, mb: 2}}>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.totalPlants)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.plantCount, locale) || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.knownPlantsWithImportedGrades)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.modifiedPlants, locale) || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.newPlantsWithImportedGrades)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.createdPlants, locale) || 0}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(AdminI18N.cartonGradesImport.column.removedPlantsWithoutGrades)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatInteger(importLog.deletedPlants, locale) || 0}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Fehlerstatus */}
                        {!importLog.success && importLog.errorMessage && (
                                <Grid size={{xs: 12}}>
                                    <Typography
                                            variant="h6">{t(AdminI18N.cartonGradesImport.page.errorStatusTitle)}</Typography>
                                    <Alert severity="error" sx={{mb: 2}}>
                                        {importLog.errorMessage}
                                    </Alert>
                                </Grid>
                        )}

                        {/* Gesamtstatus */}
                        <Grid size={{xs: 12}}>
                            <Divider sx={{my: 1}}/>
                            <Typography variant="h6">
                                {t(AdminI18N.cartonGradesImport.page.overallStatusTitle)}:{" "}
                                <Typography component="span" color={importLog.success ? "success.main" : "error.main"}>
                                    {importLog.success
                                            ? t(AdminI18N.cartonGradesImport.message.statusSuccess)
                                            : t(AdminI18N.cartonGradesImport.message.statusFailed)}
                                </Typography>
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="contained" color="primary">
                        {t(CommonI18N.action.close)}
                    </Button>
                </DialogActions>
            </Dialog>
    );
};

export default ImportLogDialog;
